import React, { useState, useRef } from 'react'
import styles from './vinSearch.module.scss'
import useTranslation from '@/hooks/useTranslation'
import { Whisper, Tooltip, Form, Loader, InputGroup, Modal, Row, Col, Text } from 'rsuite'
import cn from 'classnames'
import ShowVinInfoModal from '@/components/shared/showVinInfoModal'
import { ActionButton } from '@/components/styles'
import { RiInformation2Fill } from 'react-icons/ri'
import { useIsMobile } from '@/hooks/useIsMobile'
import DirectionalArrow from '@/components/themes/common/icons/directionalArrow'

const DefaultVinSearch = ({
  showVINInfo,
  setShowVINInfo,
  vin,
  setVin,
  setVINError,
  loadingVIN,
  VINError,
  decodeVinFn,
  showActionIcon
}) => {
  const t = useTranslation()
  const isMobile = useIsMobile()
  const [showVinHintModal, setShowVinHintModal] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const whisperRef = useRef(null)

  const vinHintText = t(
    'vin_hint_tooltip',
    "Your VIN is the 17-character vehicle identification number that can be found on your vehicle registration, insurance card, by looking through the windshield on the driver's side of the dashboard, or on a plate inside the driver's doorjamb."
  )

  const openTooltip = () => {
    whisperRef.current?.open()
    setIsTooltipOpen(true)
  }

  const closeTooltip = () => {
    whisperRef.current?.close()
    setIsTooltipOpen(false)
  }

  const handleWhisperToggle = () => {
    if (whisperRef.current?.getState?.()?.open) {
      closeTooltip()
    } else {
      openTooltip()
    }
  }

  return (
    <>
      <Row>
        <Col xs={24} md={12}>
          <Form className={styles.vinSearchForm}>
            <Form.Group>
              <Text id="enterVIN" className={cn('mt-20', styles.enterVin)} as="h2">
                {t('homepage_enter_vin_number')}
              </Text>
              <InputGroup className={styles.vinSearchInputGroup}>
                <Form.Control
                  className={styles.searchVinInput}
                  name="VIN"
                  placeholder={t('homepage_enter_vin_placeholder')}
                  maxLength={17}
                  onChange={value => {
                    setVin(value)
                    setVINError(false)
                  }}
                  value={vin}
                  aria-labelledby="enterVIN"
                  aria-invalid={!!VINError}
                  aria-describedby={VINError ? 'vin-error-message' : undefined}
                />
                {isMobile ? (
                  <InputGroup.Addon
                    className={cn(styles.noBgAddon, styles.pointerAddon)}
                    onClick={() => setShowVinHintModal(true)}
                    aria-label={t('vin_hint', 'VIN Information')}
                    tabIndex={0}
                    role="button"
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setShowVinHintModal(true)
                      }
                      if (e.key === 'Escape') {
                        setShowVinHintModal(false)
                      }
                    }}
                  >
                    <RiInformation2Fill size={24} />
                  </InputGroup.Addon>
                ) : (
                  <Whisper
                    ref={whisperRef}
                    placement="bottom"
                    trigger="none"
                    controlId="vin-hint-description"
                    speaker={<Tooltip>{vinHintText}</Tooltip>}
                  >
                    <InputGroup.Addon
                      className={cn(styles.noBgAddon, styles.pointerAddon)}
                      role="button"
                      tabIndex={0}
                      aria-label={t('vin_hint', 'VIN Information')}
                      aria-expanded={isTooltipOpen}
                      aria-describedby="vin-hint-description"
                      onClick={handleWhisperToggle}
                      onBlur={closeTooltip}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleWhisperToggle()
                        }
                        if (e.key === 'Escape') {
                          closeTooltip()
                        }
                      }}
                    >
                      <RiInformation2Fill aria-hidden="true" size={24} />
                    </InputGroup.Addon>
                  </Whisper>
                )}
              </InputGroup>
            </Form.Group>
          </Form>
        </Col>
      </Row>
      {VINError && (
        <Row>
          <Col>
            <span id="vin-error-message" role="alert" aria-live="polite" className={cn(styles.vinError)}>
              {t(VINError)}
            </span>
          </Col>
        </Row>
      )}
      <Row>
        <Col className={styles.vinSearchActionColumn}>
          <ActionButton
            $withIcon={showActionIcon}
            appearance="primary"
            onClick={decodeVinFn}
            disabled={!vin || vin.length < 17 || loadingVIN}
            className={styles.vinSearchActionButton}
          >
            {t('look_up_vehicle')} {showActionIcon && <DirectionalArrow size={16} className="fix-btn-icon" />}
            {loadingVIN && (
              <span className={styles.vinSearchLoaderInline}>
                <Loader aria-label={t('loading', 'Loading')} />
              </span>
            )}
          </ActionButton>
        </Col>
      </Row>

      <ShowVinInfoModal show={showVINInfo} onHide={() => setShowVINInfo(false)} />

      <Modal open={showVinHintModal} onClose={() => setShowVinHintModal(false)} size="xs">
        <Modal.Header>
          <Modal.Title>{t('vin_hint', 'VIN Information')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{vinHintText}</p>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default DefaultVinSearch
